import React, { useState, useEffect, useCallback } from 'react';

/**
 * VerbDashboard — Management dashboard for all verb conjugations.
 * Reads from verb_conjugations collection (existing data, 15k+ entries).
 * Shows all verbs grouped by verb_group, organized by tense (past/present/future).
 * Search, inline edit, status indicators for missing tenses.
 */

const PositiveGroupsAdmin = ({ API, adminToken, isDarkMode }) => {
  const [verbs, setVerbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTense, setActiveTense] = useState('all');
  const [expandedVerb, setExpandedVerb] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const dk = isDarkMode;

  const fetchVerbs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/grammar/verb-dashboard?`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await res.json();
      if (data.success) {
        setVerbs(data.verbs || []);
      }
    } catch (err) {
      console.error('Failed to load verb dashboard:', err);
    }
    setLoading(false);
  }, [API, adminToken, searchTerm]);

  useEffect(() => {
    fetchVerbs();
  }, [fetchVerbs]);

  const handleSaveEdit = async () => {
    if (!editEntry) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/grammar/verb-dashboard/${editEntry.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          latinScript: editEntry.latinScript,
          translatedText: editEntry.translatedText
        })
      });
      const data = await res.json();
      if (data.success) {
        setEditEntry(null);
        fetchVerbs();
      }
    } catch (err) {
      console.error('Failed to save:', err);
    }
    setSaving(false);
  };

  const filteredVerbs = statusFilter === 'all'
    ? verbs
    : verbs.filter(v => v.status === statusFilter);

  const stats = {
    total: verbs.length,
    complete: verbs.filter(v => v.status === 'complete').length,
    partial: verbs.filter(v => v.status === 'partial').length,
    missing: verbs.filter(v => v.status === 'missing').length,
  };

  const tenseLabel = { past: '⏮ Past', present: '▶ Present', future: '⏭ Future' };

  const renderEntries = (entries, tense) => {
    if (!entries || entries.length === 0) {
      return <span className={`text-xs italic ${dk ? 'text-red-400' : 'text-red-500'}`}>No {tense} forms</span>;
    }
    return (
      <div className="space-y-0.5">
        {entries.map((e, i) => (
          <div key={e.id} className={`flex items-center gap-2 text-xs py-0.5 px-1 rounded ${dk ? 'hover:bg-gray-600' : 'hover:bg-gray-50'}`}>
            <span className={`w-36 truncate ${dk ? 'text-gray-400' : 'text-gray-500'}`}>{e.sourceText}</span>
            <span className={`w-40 font-mono ${dk ? 'text-emerald-400' : 'text-emerald-700'}`}>{e.latinScript}</span>
            <span className={`w-40 font-mono text-right ${dk ? 'text-amber-400' : 'text-amber-700'}`} dir="rtl">{e.translatedText}</span>
            <button
              onClick={() => setEditEntry({ ...e })}
              className={`text-xs px-1.5 py-0.5 rounded ${dk ? 'text-blue-400 hover:bg-gray-600' : 'text-blue-600 hover:bg-blue-50'}`}
              title="Edit"
            >✏️</button>
          </div>
        ))}
      </div>
    );
  };

  const inputCls = `w-full px-2 py-1 rounded border text-sm ${dk ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`;
  const inputClsAr = `w-full px-2 py-1 rounded border text-sm text-right ${dk ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`;

  return (
    <div className={`p-6 rounded-lg border ${dk ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>

      {/* Edit Modal */}
      {editEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`p-6 rounded-lg w-full max-w-md ${dk ? 'bg-gray-800' : 'bg-white'}`}>
            <h4 className={`font-bold mb-1 ${dk ? 'text-white' : 'text-gray-800'}`}>Edit Form</h4>
            <p className={`text-xs mb-4 ${dk ? 'text-gray-400' : 'text-gray-500'}`}>{editEntry.sourceText}</p>
            <div className="space-y-3 mb-4">
              <div>
                <label className={`text-xs font-medium ${dk ? 'text-emerald-400' : 'text-emerald-600'}`}>Latin Script</label>
                <input
                  type="text"
                  value={editEntry.latinScript}
                  onChange={e => setEditEntry({ ...editEntry, latinScript: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={`text-xs font-medium ${dk ? 'text-amber-400' : 'text-amber-600'}`}>Arabic Script</label>
                <input
                  type="text"
                  dir="rtl"
                  value={editEntry.translatedText}
                  onChange={e => setEditEntry({ ...editEntry, translatedText: e.target.value })}
                  className={inputClsAr}
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditEntry(null)} className={`px-3 py-1 rounded text-sm ${dk ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}>Cancel</button>
              <button onClick={handleSaveEdit} disabled={saving} className="px-3 py-1 rounded text-sm bg-emerald-600 text-white hover:bg-emerald-700">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-lg font-bold ${dk ? 'text-white' : 'text-gray-800'}`}>
          Verb Manager
        </h3>
        <div className={`text-xs ${dk ? 'text-gray-400' : 'text-gray-500'}`}>
          {stats.total} verbs — <span className="text-emerald-500">{stats.complete} complete</span> · <span className="text-amber-500">{stats.partial} partial</span> · <span className="text-red-500">{stats.missing} missing</span>
        </div>
      </div>

      {/* Tense Tabs */}
      <div className="flex gap-2 mb-3">
        {['all', 'past', 'present', 'future'].map(t => (
          <button
            key={t}
            onClick={() => setActiveTense(t)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeTense === t
                ? 'bg-emerald-600 text-white'
                : dk ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t === 'all' ? '📋 All Tenses' : tenseLabel[t]}
          </button>
        ))}
      </div>

      {/* Search + Status Filter */}
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search verbs..."
          className={`flex-1 px-3 py-2 rounded-lg border text-sm ${dk ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-400'}`}
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className={`px-3 py-2 rounded-lg border text-sm ${dk ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
        >
          <option value="all">All Status</option>
          <option value="complete">✅ Complete</option>
          <option value="partial">⚠️ Partial</option>
          <option value="missing">❌ Missing</option>
        </select>
      </div>

      {/* Loading */}
      {loading && (
        <div className={`text-center py-8 ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Loading verbs...</div>
      )}

      {/* Verb List */}
      {!loading && filteredVerbs.length === 0 && (
        <div className={`text-center py-8 ${dk ? 'text-gray-400' : 'text-gray-500'}`}>No verbs found</div>
      )}

      {!loading && filteredVerbs.length > 0 && (
        <div className="space-y-1">
          {filteredVerbs.map(v => {
            const isExpanded = expandedVerb === v.verb_group;
            const statusIcon = v.status === 'complete' ? '✅' : v.status === 'partial' ? '⚠️' : '❌';
            const missingText = v.missing_tenses.length > 0 ? `Missing: ${v.missing_tenses.join(', ')}` : '';

            return (
              <div key={v.verb_group} className={`rounded-lg border ${dk ? 'border-gray-700' : 'border-gray-200'}`}>
                {/* Verb Header — click to expand */}
                <button
                  onClick={() => setExpandedVerb(isExpanded ? null : v.verb_group)}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-left rounded-lg transition-colors ${
                    isExpanded
                      ? dk ? 'bg-gray-700' : 'bg-emerald-50'
                      : dk ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm">{statusIcon}</span>
                    <span className={`font-semibold text-sm ${dk ? 'text-white' : 'text-gray-800'}`}>{v.verb_group}</span>
                    <span className={`text-xs ${dk ? 'text-gray-500' : 'text-gray-400'}`}>
                      {v.past.length + v.present.length + v.future.length} forms
                    </span>
                    {missingText && (
                      <span className={`text-xs ${dk ? 'text-red-400' : 'text-red-500'}`}>{missingText}</span>
                    )}
                  </div>
                  <span className={`text-xs ${dk ? 'text-gray-500' : 'text-gray-400'}`}>{isExpanded ? '▼' : '▶'}</span>
                </button>

                {/* Expanded — show all tenses */}
                {isExpanded && (
                  <div className={`px-4 pb-4 pt-2 ${dk ? 'bg-gray-750' : 'bg-gray-50'} rounded-b-lg`}>
                    {(activeTense === 'all' ? ['past', 'present', 'future'] : [activeTense]).map(t => (
                      <div key={t} className="mb-3 last:mb-0">
                        <h5 className={`text-xs font-bold uppercase tracking-wider mb-1 ${dk ? 'text-gray-400' : 'text-gray-500'}`}>
                          {tenseLabel[t] || t}
                        </h5>
                        {renderEntries(v[t], t)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PositiveGroupsAdmin;
